import type {
  Status,
  UpdateBulkPlayerTeamRequest
} from '@library/types/projection';
import { useUpdateBulkPlayerProjectionsMutation } from '@bo-services/projectionsApi';

export interface TradingPlayerBulkActions {
  handleChangeBoardTime: (boardTime: string) => Promise<void>;
  handleChangeStartTime: (boardTime: string) => Promise<void>;
  handleChangeStatus: (status: Status) => Promise<void>;
  handleChangeInGame: (inGame: boolean) => Promise<void>;
  handleChangeGameModes: (gameModeIds: Array<number>) => Promise<void>;
}

export const useTradingPlayerBulkActions = ({
  leagueId,
  playerIds,
  gameId
}: {
  leagueId: string;
  playerIds: Array<string>;
  gameId: string;
}) => {
  const [
    patchBulkPlayer,
    {
      error: bulkUpdatePlayerError,
      isError: isBulkUpdatePlayerError,
      isSuccess: isBulkUpdatePlayerSuccess,
      isUninitialized: isBulkUpdatePlayerUninitialized,
      reset: resetBulkUpdatePlayer
    }
  ] = useUpdateBulkPlayerProjectionsMutation();

  const bulkUpdatePlayer = async ({
    boardTime,
    gameModeIds,
    inGame,
    startTime,
    status
  }: {
    boardTime?: string;
    gameModeIds?: Array<number>;
    inGame?: boolean;
    startTime?: string;
    status?: Status;
  }) => {
    // If NLS, needs game start time array
    const request: UpdateBulkPlayerTeamRequest = {
      bulk_request: {
        game_id: gameId,
        player_ids: playerIds,
        projections: [
          {
            board_time: boardTime,
            game_mode_ids: gameModeIds,
            in_game: inGame,
            league_id: leagueId,
            start_time: startTime,
            status
          }
        ]
      }
    };

    await patchBulkPlayer(request).catch((error) => {
      // eslint-disable-next-line no-console
      console.log('Error Bulk Updating Projections', error);
    });
  };

  const handleChangeBoardTime = async (boardTime: string) => {
    await bulkUpdatePlayer({ boardTime });
  };

  const handleChangeStartTime = async (startTime: string) => {
    await bulkUpdatePlayer({ startTime });
  };

  const handleChangeStatus = async (status: Status) => {
    await bulkUpdatePlayer({ status });
  };

  const handleChangeInGame = async (inGame: boolean) => {
    await bulkUpdatePlayer({ inGame });
  };

  const handleChangeGameModes = async (gameModeIds: Array<number>) => {
    await bulkUpdatePlayer({ gameModeIds });
  };

  const tradingPlayerBulkActions: TradingPlayerBulkActions = {
    handleChangeBoardTime,
    handleChangeStartTime,
    handleChangeStatus,
    handleChangeInGame,
    handleChangeGameModes
  };

  return {
    tradingPlayerBulkActions,
    bulkUpdatePlayerError,
    isBulkUpdatePlayerError,
    isBulkUpdatePlayerSuccess,
    isBulkUpdatePlayerUninitialized,
    resetBulkUpdatePlayer
  };
};
